import { differenceInDays } from 'date-fns';
import type { AppData, SavingsGoal, ContributionFrequency } from '@/lib/types';

/** Number of days per frequency cycle */
function frequencyDays(freq: ContributionFrequency): number {
  switch (freq) {
    case 'weekly': return 7;
    case 'fortnightly': return 14;
    case 'monthly': return 30;
    default: return 0;
  }
}

/**
 * Process any due automatic contributions for all savings goals.
 * Returns updated AppData if changes were made, or the same object if none.
 */
export function processAutoContributions(data: AppData): AppData {
  const goals = data.savingsGoals ?? [];
  const today = new Date().toISOString().split('T')[0];
  let changed = false;

  const updatedGoals = goals.map((g): SavingsGoal => {
    if (
      !g.contributionFrequency ||
      g.contributionFrequency === 'none' ||
      !g.contributionAmount ||
      g.contributionAmount <= 0 ||
      g.currentAmount >= g.targetAmount
    ) {
      return g;
    }

    const cycleDays = frequencyDays(g.contributionFrequency);
    if (cycleDays === 0) return g;

    const lastDate = g.lastContributionDate || g.createdAt.split('T')[0];
    const daysSinceLast = differenceInDays(new Date(today), new Date(lastDate));

    if (daysSinceLast < cycleDays) return g;

    // Calculate how many cycles have elapsed
    const cycles = Math.floor(daysSinceLast / cycleDays);
    if (cycles <= 0) return g;

    const totalContribution = cycles * g.contributionAmount;
    // Don't exceed target
    const newAmount = Math.min(g.currentAmount + totalContribution, g.targetAmount);

    changed = true;
    return {
      ...g,
      currentAmount: Math.round(newAmount * 100) / 100,
      lastContributionDate: today,
    };
  });

  if (!changed) return data;

  return { ...data, savingsGoals: updatedGoals };
}
